/**
 * The mosque's YouTube channel (A3, owner 2026-10-24): a live stream that makes its own link.
 *
 *  - <YouTubeConnect/> on «Nettside», beside «Facebook og Instagram»: an administrator (settings.write) connects the
 *    mosque's OWN YouTube channel — logging in at Google, never here. Coming back, the page reads `?youtube=` from the
 *    address and says what happened. Google's return connects nothing by itself: the channel waits until the same
 *    administrator answers «Koble til kanalen <title>?» with Bekreft (or Avbryt). Disconnecting deletes the tokens and
 *    the stream key at once.
 *  - <YouTubeLiveCard/> on each event: «Direktesending på YouTube (lenken lages automatisk)», public or unlisted, the
 *    stage (Planlagt / Klar / Direkte / Ferdig), the watch link, the stream key behind two clicks (each look is
 *    logged), the RTMP address, and what to do on the phone.
 * Shown only where the mosque has `youtube_live_auto`.
 */
import React, { useEffect, useState } from 'react';
import { call, errText, patch, post, send, type Body } from '../lib/api';
import { useMsg } from '../lib/hooks';
import { Btn, Card, CopyField, Msg, Tag, TwoStep, type TagTone } from './ui';

const OUTCOME: Record<string, [string, 'ok' | 'err']> = {
  pending: ['Google sendte deg tilbake. Se over kanalen under, og bekreft at det er moskeens.', 'ok'],
  connected: ['Koblet til. Huk av «Direktesending på YouTube» på et arrangement, så lages lenken når det publiseres.', 'ok'],
  connected_no_stream: ['Kanalen er koblet til, men YouTube laget ikke strømmen ennå. Slå på direktesending i YouTube Studio (første gang tar det opptil 24 timer), og trykk «Lag strøm».', 'err'],
  cancelled: ['Tilkoblingen ble avbrutt hos Google. Ingenting er endret.', 'err'],
  expired: ['Tilkoblingen tok for lang tid. Prøv igjen.', 'err'],
  failed: ['Google svarte ikke som ventet. Prøv igjen om litt.', 'err'],
  no_channel: ['Google-kontoen du logget inn med, har ingen YouTube-kanal. Logg inn med kontoen som eier moskeens kanal.', 'err'],
};

/** The word YouTube's stage gets, and its colour. */
export const STAGE_TAG: Record<string, [TagTone, string]> = {
  venter: ['grey', 'Venter på publisering'], planlagt: ['gold', 'Planlagt'], klar: ['green', 'Klar'], direkte: ['red', 'Direkte'],
  ferdig: ['grey', 'Ferdig'], feil: ['red', 'Feil'],
};
/** What a staff member does on the phone, in order (core youtube-live STEPS_NO). */
export const PHONE_STEPS = ['Åpne YouTube-appen og trykk «Opprett» (+).', 'Velg «Gå live».', 'Trykk «Administrer» og velg sendingen for arrangementet.', 'Trykk «Gå live». Sendingen starter selv når bildet kommer inn.'];

/** `?youtube=<outcome>` from the address Google sent the browser back to, then gone from the address. */
function takeOutcome(): string | null {
  const [path, q] = location.hash.replace(/^#/, '').split('?');
  const v = new URLSearchParams(q || '').get('youtube');
  if (v) history.replaceState(null, '', `#${path}`);
  return v;
}

/** The stream key, behind two clicks; each look is in the activity log. */
function StreamKey({ rtmpUrl }: { rtmpUrl: string | null }) {
  const [key, setKey] = useState<string | null>(null);
  const [msg, say] = useMsg();
  const reveal = async () => {
    const r = await post('/youtube/stream-key');
    if (!r.ok) return say(errText(r.body), 'err');
    setKey(r.body.key);
  };
  return (
    <>
      {rtmpUrl && <CopyField label="RTMP-adresse (for kamera eller encoder-app)" value={rtmpUrl} />}
      {key ? <CopyField label="Strømmenøkkel — hold den hemmelig" value={key} />
        : <div className="row"><TwoStep label="Vis strømmenøkkel" confirmLabel="Klikk igjen: vises her og logges" onConfirm={reveal} /></div>}
      <Msg text={msg.text} tone={msg.tone} />
    </>
  );
}

export function YouTubeConnect() {
  const [st, setSt] = useState<Body | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const load = async () => { try { setSt(await call('/youtube')); } catch { setSt({ error: true }); } };
  useEffect(() => {
    const o = takeOutcome();
    if (o && OUTCOME[o]) say(OUTCOME[o][0], OUTCOME[o][1]);
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function connect() {
    if (busy) return;
    setBusy(true);
    const r = await post('/youtube/connect');
    if (!r.ok) { setBusy(false); return say(errText(r.body), 'err'); }
    location.href = r.body.url;   // to Google's own login; it comes back to «Nettside»
  }
  async function makeStream() {
    setBusy(true);
    const r = await post('/youtube/stream').finally(() => setBusy(false));
    say(r.ok ? 'Strømmen er laget.' : errText(r.body), r.ok ? 'ok' : 'err');
    void load();
  }
  /** The channel Google returned is connected only on this person's own yes. */
  async function confirmPending() {
    setBusy(true);
    const r = await post('/youtube/confirm').finally(() => setBusy(false));
    if (!r.ok) { say(errText(r.body), 'err'); return void load(); }
    say(r.body.stream ? OUTCOME.connected[0] : OUTCOME.connected_no_stream[0], r.body.stream ? 'ok' : 'err');
    setSt(r.body);
  }
  async function cancelPending() {
    setBusy(true);
    const r = await post('/youtube/cancel').finally(() => setBusy(false));
    say(r.ok ? 'Avbrutt. Kanalen ble ikke koblet til, og tilgangen er trukket tilbake hos Google.' : errText(r.body), r.ok ? 'ok' : 'err');
    void load();
  }
  async function disconnect() {
    const r = await send('/youtube', { method: 'DELETE' });
    say(r.ok ? r.body.message_no : errText(r.body), r.ok ? 'ok' : 'err');
    void load();
  }

  if (!st || st.error) return null;
  return (
    <Card title="YouTube" hint="Koble til moskeens egen YouTube-kanal, så lager Masjid direktesendingen og lenken selv for arrangementer som er huket av. Du logger inn hos Google — passordet ditt kommer aldri hit.">
      <div className="card-body">
        {st.pending && st.canConnect && (
          <div className="row" role="group" aria-label="Bekreft kanalen" style={{ alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span><b>{st.pending.question_no}</b></span>
            <Btn disabled={busy} onClick={confirmPending}>Bekreft</Btn>
            <Btn ghost disabled={busy} onClick={cancelPending}>Avbryt</Btn>
          </div>
        )}
        {st.connected ? (
          <>
            <div className="row" style={{ alignItems: 'center', gap: 10 }}>
              <Tag tone="green">tilkoblet</Tag>
              <span>Kanal: <a href={st.channel.url} target="_blank" rel="noreferrer"><b>{st.channel.title}</b></a></span>
              {st.stream ? <span className="sub">· strøm klar ({st.stream.keyMasked})</span> : <Tag tone="gold">ingen strøm ennå</Tag>}
            </div>
            {st.error && <p className="field note" role="alert">{st.error.message_no}</p>}
            {st.stream ? (st.canStream && <StreamKey rtmpUrl={st.stream.rtmpUrl} />)
              : st.canStream && <div className="row" style={{ marginTop: 10 }}><Btn disabled={busy} onClick={makeStream}>Lag strøm</Btn></div>}
            {st.canConnect && <div className="row" style={{ marginTop: 12 }}>
              <Btn ghost disabled={busy} onClick={connect}>Bytt kanal</Btn>
              <TwoStep label="Koble fra" confirmLabel="Klikk igjen: tilgangen slettes her og hos Google" onConfirm={disconnect} />
            </div>}
          </>
        ) : st.canConnect ? (
          <div className="row"><Btn disabled={busy} aria-busy={busy || undefined} onClick={connect}>{busy ? 'Sender deg til Google …' : 'Koble til YouTube-kanal'}</Btn></div>
        ) : <p className="field note">Ikke koblet til. En administrator kobler til moskeens kanal her.</p>}
        <ul className="note">{(st.facts_no || []).map((f: string) => <li key={f}>{f}</li>)}</ul>
        <Msg text={msg.text} tone={msg.tone} />
      </div>
    </Card>
  );
}

/** «Direktesending» on one event: the tick, the privacy, the stage, the watch link, the key, the phone steps. */
export function YouTubeLiveCard({ event, onSaved }: { event: Body; onSaved: () => void }) {
  const [data, setData] = useState<Body | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, say] = useMsg();
  const load = async () => { setFailed(null); try { setData(await call(`/events/${event.id}/youtube`)); } catch (x) { setFailed((x as Error).message); } };
  const change = async (body: Body) => {
    setBusy(true);
    const r = await patch(`/events/${event.id}`, body).finally(() => setBusy(false));
    if (!r.ok) return say(errText(r.body, { youtube_auto: 'Direktesending', youtube_privacy: 'Synlighet på YouTube' }), 'err');
    say(r.body.youtube && r.body.youtube.deleted ? 'Sendingen er fjernet hos YouTube.' : r.body.youtube && r.body.youtube.queued ? 'Lenken lages nå — sjekk igjen om et minutt.' : 'Lagret.', 'ok');
    void load(); onSaved();
  };
  const [tone, label]: [TagTone, string] = data ? (STAGE_TAG[data.stage] || ['grey', data.label_no]) : ['grey', ''];
  return <details onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !data) void load(); }}>
    <summary>Direktesending</summary>
    {failed ? <p className="note" role="alert">Kunne ikke hente direktesendingen. <Btn ghost onClick={load}>Prøv igjen</Btn></p>
      : !data ? <p className="note" role="status">Henter …</p> : (
      <>
        {!data.connected && <p className="note">Ingen YouTube-kanal er koblet til. En administrator kobler til under <a href="#/nettside">Nettside</a>.</p>}
        <label className="check"><input type="checkbox" checked={data.auto} disabled={busy || !data.connected}
          onChange={(e) => void change({ youtube_auto: e.target.checked })} /> Direktesending på YouTube (lenken lages automatisk)</label>
        <div className="row" role="radiogroup" aria-label="Synlighet på YouTube">
          {(['public', 'unlisted'] as const).map((p) => (
            <label key={p} className="check"><input type="radio" name={`yt-privacy-${event.id}`} checked={data.privacy === p} disabled={busy}
              onChange={() => void change({ youtube_privacy: p })} /> {p === 'public' ? 'Offentlig' : 'Uoppført (bare med lenken)'}</label>))}
        </div>
        {data.auto && <div className="row" style={{ alignItems: 'center', gap: 10 }}>
          <Tag tone={tone}>{label}</Tag>
          {data.watchUrl && <a href={data.watchUrl} target="_blank" rel="noreferrer">{data.watchUrl}</a>}
        </div>}
        {data.error && <p className="field note" role="alert">{data.error.message_no}</p>}
        {data.auto && data.connected && <>
          <p className="note" style={{ marginBottom: 4 }}>Fra telefonen:</p>
          <ol className="note">{PHONE_STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
          {data.stream && <StreamKey rtmpUrl={data.stream.rtmpUrl} />}
          <ul className="note">{(data.facts_no || []).map((f: string) => <li key={f}>{f}</li>)}</ul>
        </>}
        <Msg text={msg.text} tone={msg.tone} />
      </>
    )}
  </details>;
}
